import { clearItem, getItem, setItem } from 'fitify-ui/src/utils/storage'

import { PlanType, type SubscriptionPlanSchema } from '../types/subscription'

import { logEvent } from './analytics'
import { getSubscribePredictedValue } from './retentions/subscribe-predicted-value'

const PENDING_SUBSCRIBE_EVENT_KEY = '@fitify/subscription/pendingSubscribeEvent'

export type SubscribeEventPayload = {
  currency?: string
  intervalLength?: number
  intervalUnit?: string
  isTrial?: boolean
  priceTotalValue?: number
  sku?: string
  totalValue?: number
  transactionId?: string
  type?: PlanType
}

export const buildSubscribeEventPayload = ({
  plan,
  transactionId,
}: {
  plan?: Pick<
    SubscriptionPlanSchema,
    | 'currency'
    | 'intervalLength'
    | 'intervalUnit'
    | 'priceTotalValue'
    | 'sku'
    | 'totalValue'
    | 'type'
  >
  transactionId?: string
}): SubscribeEventPayload => ({
  currency: plan?.currency,
  intervalLength: plan?.intervalLength,
  intervalUnit: plan?.intervalUnit,
  isTrial: plan?.type === PlanType.TRIAL,
  priceTotalValue: plan?.priceTotalValue,
  sku: plan?.sku,
  totalValue: plan?.totalValue,
  transactionId,
  type: plan?.type,
})

export const logSubscribeEvent = (payload: SubscribeEventPayload) => {
  logEvent(payload.isTrial ? 'start_trial' : 'subscribe', {
    value: getSubscribePredictedValue({
      intervalLength: payload.intervalLength,
      intervalUnit: payload.intervalUnit,
      priceTotalValue: payload.priceTotalValue ?? 0,
      totalValue: payload.totalValue ?? 0,
      type: payload.type,
    }),
    currency: payload.currency || 'USD',
    content_id: payload.sku,
    transaction_id: payload.transactionId,
  })
}

export const setPendingSubscribeEvent = (payload: SubscribeEventPayload) => {
  try {
    setItem('session', PENDING_SUBSCRIBE_EVENT_KEY, JSON.stringify(payload))
  } catch (error) {
    console.error('Failed to store pending subscribe event', error)
  }
}

// Clears before returning so a refresh of the return page cannot double-count.
export const consumePendingSubscribeEvent =
  (): SubscribeEventPayload | null => {
    const stored = getItem('session', PENDING_SUBSCRIBE_EVENT_KEY)
    clearItem('session', PENDING_SUBSCRIBE_EVENT_KEY)

    if (!stored) {
      return null
    }

    try {
      return JSON.parse(stored) as SubscribeEventPayload
    } catch (error) {
      console.error('Failed to parse pending subscribe event', error)
      return null
    }
  }
